import { Box, Icon, Text } from '@chakra-ui/react';
import type { IconType } from 'react-icons';

export interface StatCardProps {
  label: string;
  value: string | number;
  subtext?: string;
  icon?: IconType;
  colorPalette?: string;
  'data-e2e'?: string;
}

/**
 * @useFor a KPI / metric tile on a dashboard — single big number +
 *   label + optional icon + optional subtext (e.g. "+ 12% vs last
 *   week").
 *
 *   Examples in-tree: AIM dashboards (Active alerts, Resolved this
 *   week), Onboarding stats, Reports summary tiles.
 *
 *   For clickable summary cards use `<CardComponent>`. For grouped
 *   form sections use `<SectionCard>`. StatCard is read-only by
 *   design — no `onClick` — to keep the dashboard semantics clean.
 */
export function StatCard({
  label,
  value,
  subtext,
  icon,
  colorPalette = 'gray',
  'data-e2e': dataE2e,
}: StatCardProps) {
  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.secondary"
      borderRadius="lg"
      padding="16px"
      data-e2e={dataE2e}
    >
      {icon && (
        <Icon as={icon} boxSize={5} color={`${colorPalette}.500`} mb={2} />
      )}
      <Text fontSize="12px" color="text.secondary" fontWeight={500} mb={1}>
        {label}
      </Text>
      <Text
        fontSize="24px"
        fontWeight={700}
        color="text.primary"
        data-e2e={dataE2e ? `${dataE2e}-value` : undefined}
      >
        {value}
      </Text>
      {subtext && (
        <Text fontSize="12px" color="text.secondary" mt={1}>
          {subtext}
        </Text>
      )}
    </Box>
  );
}
